/**
 * URL redaction for analytics payloads.
 *
 * Privacy Policy §8 commits that product analytics captures "account and product-use events rather than
 * document contents or other identifiable case, tenant, SCRA, or Customer Data". Several apps put an opaque
 * identifier directly in the URL path, and posthog-js ships the full URL as `$current_url` (and derives
 * `$initial_current_url` person properties from it), so without this the identifier lands in a third-party
 * store:
 *  - `ee-tracker` — the path IS the eviction id.
 *  - `ee-email-settings` — the path IS the client's `notificationKey`, an unauthenticated capability that
 *    grants both read AND write of their notification settings. That one is a security concern, not just a
 *    privacy one; redacting here stops the leak, but the key belongs out of the URL path entirely.
 *  - `ee-portal` — case/eviction/invoice ObjectIds appear throughout the route tree.
 *
 * Non-opaque path segments, the query string and the fragment are preserved so funnels and pathname
 * breakdowns still work. Absolute URLs are re-emitted via `URL.toString()`, which may normalize host
 * casing / percent-encoding — the redaction guarantee is structural (opaque segments gone), not
 * byte-identical re-serialization of the input string.
 */

/** Replaces a redacted path segment. Deliberately not a valid id, so a stray un-redacted value is obvious. */
export const REDACTED_SEGMENT = ':redacted';

/** Mongo ObjectId — the id shape used by every EasyEviction entity. */
const OBJECT_ID = /^[0-9a-fA-F]{24}$/;

/** UUID (any version), e.g. bulk-import session ids and PostHog's own identifiers. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Long opaque token — covers the 32-char `notificationKey` (Base64 of an ObjectId hex string, so always
 * `[A-Za-z0-9]`). Requiring 20+ characters with no `-`/`_` cannot swallow a route name: every route segment
 * in the client that is 14 characters or longer is kebab-case (`dependent-subscriptions`,
 * `intake-form-customizer`, `lawpay-reconciliation`, …), and there is no route word of this length without a
 * dash or underscore. Verify that still holds before adding a long single-word route.
 */
const OPAQUE_TOKEN = /^[A-Za-z0-9]{20,}$/;

function isOpaque(segment: string): boolean {
  return OBJECT_ID.test(segment) || UUID.test(segment) || OPAQUE_TOKEN.test(segment);
}

/**
 * Replaces opaque path segments in `url` with {@link REDACTED_SEGMENT}, leaving the origin, the remaining
 * path, the query string and the fragment untouched. Non-URL strings are returned unchanged.
 */
export function redactOpaqueSegments(url: string): string {
  if (!url) {
    return url;
  }
  // Relative values (`$pathname`-style) have no origin, so parse against a throwaway base and re-emit only
  // what we were given.
  let parsed: URL;
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  try {
    parsed = isAbsolute ? new URL(url) : new URL(url, 'https://redaction.invalid');
  } catch {
    return url;
  }

  const redactedPath = parsed.pathname
    .split('/')
    .map(segment => (isOpaque(segment) ? REDACTED_SEGMENT : segment))
    .join('/');
  if (redactedPath === parsed.pathname) {
    return url;
  }
  parsed.pathname = redactedPath;
  return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Property names whose values are URLs. `$set`/`$set_once` are walked separately because posthog derives
 * `$initial_current_url` / `$initial_referrer` person properties from the same URLs — scrubbing only the
 * top level would leave a copy behind on the person record.
 */
const URL_PROPERTIES = ['$current_url', '$referrer', '$pathname', '$prev_pageview_pathname'];

function redactUrlProperties(properties: Record<string, unknown> | undefined): void {
  if (!properties) {
    return;
  }
  for (const key of Object.keys(properties)) {
    const value = properties[key];
    if (typeof value !== 'string') {
      continue;
    }
    // Match by name for the known URL properties, and by shape for the `$initial_*` mirrors of them.
    if (URL_PROPERTIES.includes(key) || /url|pathname|referrer/i.test(key)) {
      properties[key] = redactOpaqueSegments(value);
    }
  }
}

/**
 * `before_send` hook: redacts opaque path segments from every URL-bearing property of an outgoing event,
 * including the nested `$set` / `$set_once` person-property bags. Mutates and returns the same object —
 * returning `null` would drop the event, which is never what we want here.
 */
export function redactAnalyticsUrls<T extends { properties?: Record<string, unknown> } | null>(
  captureResult: T
): T {
  if (!captureResult) {
    return captureResult;
  }
  const bags = [
    captureResult.properties,
    captureResult.properties?.['$set'] as Record<string, unknown> | undefined,
    captureResult.properties?.['$set_once'] as Record<string, unknown> | undefined,
    (captureResult as { $set?: Record<string, unknown> }).$set,
    (captureResult as { $set_once?: Record<string, unknown> }).$set_once
  ];
  for (const bag of bags) {
    redactUrlProperties(bag);
  }
  return captureResult;
}
